"use client";

import type { ChangeEvent } from "react";
import type { TranscriptionResponse } from "@ui/components/demos/STT/types";
import type { UploadFn, UrlTranscribeFn } from "./useSTTHeroPlayback";
import type { UploadedMediaKind } from "../helpers/sttHeroPlaybackUtils";

import { useCallback, useEffect, useReducer, useRef } from "react";
import { useTranslations } from "next-intl";

import { toast } from "@/app/_providers/toast";
import { captureEvent } from "@/app/_analytics/analytics";
import { createVideoThumbnail } from "@/app/_utilities/videoUtils";
import { EventName } from "@mw/shared/analyticsEventNames";
import { MEDIA_CONTENT_TYPES } from "@ui/components/demos/STT/utils";
import { logger } from "@utilities/logger";
import { getTranscriptionErrorStatus, getUrlDisplayName, revokeBlobAudioUrl } from "../helpers/sttHeroPlaybackUtils";

interface UploadState {
  transcription: TranscriptionResponse | null;
  thumbnailUrl: string | null;
  audioUrl: string | null;
  isUploading: boolean;
  fileName: string | null;
  mediaKind: UploadedMediaKind | null;
  showQuotaExceeded: boolean;
}

type UploadAction =
  | { type: "reset" }
  | { type: "start"; fileName: string; mediaKind: UploadedMediaKind; audioUrl: string | null }
  | { type: "setThumbnail"; thumbnailUrl: string }
  | { type: "setTranscription"; transcription: TranscriptionResponse }
  | { type: "setUrlResult"; transcription: TranscriptionResponse; audioUrl: string; fileName: string }
  | { type: "fail"; clearAudioUrl: boolean; showQuotaExceeded: boolean }
  | { type: "finish" };

const initialUploadState: UploadState = {
  transcription: null,
  thumbnailUrl: null,
  audioUrl: null,
  isUploading: false,
  fileName: null,
  mediaKind: null,
  showQuotaExceeded: false,
};

const uploadReducer = (state: UploadState, action: UploadAction) => {
  switch (action.type) {
    case "reset":
      return initialUploadState;
    case "start":
      return {
        transcription: null,
        thumbnailUrl: null,
        audioUrl: action.audioUrl,
        isUploading: true,
        fileName: action.fileName,
        mediaKind: action.mediaKind,
        showQuotaExceeded: false,
      };
    case "setThumbnail":
      return { ...state, thumbnailUrl: action.thumbnailUrl };
    case "setTranscription":
      return { ...state, transcription: action.transcription };
    case "setUrlResult":
      return {
        ...state,
        transcription: action.transcription,
        audioUrl: action.audioUrl,
        fileName: action.fileName,
      };
    case "fail":
      return {
        ...state,
        transcription: null,
        audioUrl: action.clearAudioUrl ? null : state.audioUrl,
        showQuotaExceeded: action.showQuotaExceeded,
      };
    case "finish":
      return { ...state, isUploading: false };
  }
};

interface UseSTTHeroUploadOptions {
  uploadFn: UploadFn;
  urlTranscribeFn: UrlTranscribeFn;
}

export function useSTTHeroUpload({ uploadFn, urlTranscribeFn }: UseSTTHeroUploadOptions) {
  const t = useTranslations("ClientKeys.STTHero");
  const [state, dispatch] = useReducer(uploadReducer, initialUploadState);
  const uploadedAudioUrlRef = useRef<string | null>(null);
  const uploadRequestIdRef = useRef(0);

  const clearUploadedAudioUrl = useCallback(() => {
    revokeBlobAudioUrl(uploadedAudioUrlRef.current);
    uploadedAudioUrlRef.current = null;
  }, []);

  const resetUploadState = useCallback(() => {
    clearUploadedAudioUrl();
    uploadRequestIdRef.current += 1;
    dispatch({ type: "reset" });
  }, [clearUploadedAudioUrl]);

  const beginUploadRequest = useCallback(
    ({ fileName, mediaKind, audioUrl }: { fileName: string; mediaKind: UploadedMediaKind; audioUrl?: string }) => {
      uploadRequestIdRef.current += 1;
      const uploadRequestId = uploadRequestIdRef.current;

      clearUploadedAudioUrl();
      if (audioUrl) {
        uploadedAudioUrlRef.current = audioUrl;
      }
      dispatch({ type: "start", fileName, mediaKind, audioUrl: audioUrl ?? null });

      return uploadRequestId;
    },
    [clearUploadedAudioUrl],
  );

  const reportQuotaExceeded = useCallback((errorStatus: string) => {
    if (errorStatus !== "quota_exceeded") return false;

    captureEvent(EventName.mw_stt_quota_exceeded, { source: "stt_hero" });
    return true;
  }, []);

  useEffect(() => {
    return () => {
      clearUploadedAudioUrl();
    };
  }, [clearUploadedAudioUrl]);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!MEDIA_CONTENT_TYPES.includes(file.type)) {
        logger.warn(`[useSTTHeroPlayback] Unsupported file type: ${file.type}`);
        return;
      }

      const isVideoUpload = file.type.startsWith("video/");
      const objectUrl = URL.createObjectURL(file);
      const uploadRequestId = beginUploadRequest({
        fileName: file.name,
        mediaKind: isVideoUpload ? "video" : "audio",
        audioUrl: objectUrl,
      });

      if (isVideoUpload) {
        void createVideoThumbnail(file)
          .then(thumbnailUrl => {
            if (uploadRequestIdRef.current !== uploadRequestId) return;
            dispatch({ type: "setThumbnail", thumbnailUrl });
          })
          .catch(error => {
            logger.error(
              error instanceof Error ? error : null,
              "[useSTTHeroPlayback] Failed to generate uploaded video thumbnail",
            );
          });
      }

      captureEvent(EventName.mw_stt_transcribe_file, { source: "stt_hero" });

      try {
        const transcription = await uploadFn(file);
        if (uploadRequestIdRef.current !== uploadRequestId) return;

        dispatch({ type: "setTranscription", transcription });
      } catch (error) {
        if (uploadRequestIdRef.current !== uploadRequestId) return;

        logger.error(error instanceof Error ? error : null, "[useSTTHeroPlayback] File upload transcription failed");
        const errorStatus = getTranscriptionErrorStatus(error);
        captureEvent(EventName.mw_stt_transcribe_file_error, {
          source: "stt_hero",
          error_status: errorStatus,
        });
        dispatch({ type: "fail", clearAudioUrl: false, showQuotaExceeded: reportQuotaExceeded(errorStatus) });
      } finally {
        if (uploadRequestIdRef.current === uploadRequestId) {
          dispatch({ type: "finish" });
        }
        // Reset the file input so the same file can be re-selected
        e.target.value = "";
      }
    },
    [beginUploadRequest, reportQuotaExceeded, uploadFn],
  );

  const handleUrlSubmit = useCallback(
    async (urlString: string) => {
      const trimmed = urlString.trim();
      if (!trimmed) return;

      const displayName = getUrlDisplayName(trimmed);
      const uploadRequestId = beginUploadRequest({ fileName: displayName, mediaKind: "audio" });

      captureEvent(EventName.mw_stt_transcribe_url, { url: trimmed, source: "stt_hero" });

      try {
        const { transcription, audioUrl, sourceTitle } = await urlTranscribeFn(trimmed);
        if (uploadRequestIdRef.current !== uploadRequestId) return;

        dispatch({
          type: "setUrlResult",
          transcription,
          audioUrl,
          fileName: sourceTitle || displayName,
        });
      } catch (error) {
        if (uploadRequestIdRef.current !== uploadRequestId) return;

        logger.error(error instanceof Error ? error : null, "[useSTTHeroPlayback] URL transcription failed");
        const errorStatus = getTranscriptionErrorStatus(error);
        captureEvent(EventName.mw_stt_transcribe_url_error, {
          transcribe_url: trimmed,
          transcribe_error: error,
          source: "stt_hero",
        });
        if (reportQuotaExceeded(errorStatus)) {
          dispatch({ type: "fail", clearAudioUrl: true, showQuotaExceeded: true });
        } else {
          resetUploadState();
          toast.error(t("transcription_failed"));
        }
      } finally {
        if (uploadRequestIdRef.current === uploadRequestId) {
          dispatch({ type: "finish" });
        }
      }
    },
    [beginUploadRequest, reportQuotaExceeded, resetUploadState, t, urlTranscribeFn],
  );

  return {
    ...state,
    resetUploadState,
    handleFileChange,
    handleUrlSubmit,
  };
}
